import React, { useState, useMemo } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { StatRow, Modal, Field, Input, Select, Btn, MedioBadge, TipoBadge, SectionHeader } from '../components/UI';
import { fmtM, fmtDate, fmtDateShort, todayStr, ventasMes, promedioDiario, exportToCSV } from '../utils';
import { showToast } from '../components/UI';

export default function Ventas({ store, setItem, delItem }) {
  const { movimientos } = store;
  const [modal, setModal] = useState(false);

  const [form, setForm] = useState({});
  const [vista, setVista] = useState('semana');
  const [filtroMes, setFiltroMes] = useState(todayStr().slice(0, 7));

  const hoy = todayStr();
  const prom20 = useMemo(() => promedioDiario(movimientos, 20), [movimientos]);
  const mesMonto = useMemo(() => ventasMes(movimientos, filtroMes), [movimientos, filtroMes]);

  // Chart data
  const chartData = useMemo(() => {
    if (vista === 'semana') {
      const dias = {};
      for (let i = 6; i >= 0; i--) {
        const d = new Date(Date.now() - i * 86400000);
        const key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
        dias[key] = 0;
      }
      Object.values(movimientos || {}).filter(m => m.tipo === 'venta' && dias[m.fecha] !== undefined).forEach(m => {
        dias[m.fecha] = (dias[m.fecha] || 0) + (parseFloat(m.monto) || 0);
      });
      return Object.entries(dias).map(([date, ventas]) => ({ date: fmtDateShort(date), ventas: Math.round(ventas), prom: Math.round(prom20) }));
    }
    if (vista === 'mes') {
      const semanas = {};
      Object.values(movimientos || {}).filter(m => m.tipo === 'venta' && m.fecha?.slice(0, 7) === filtroMes).forEach(m => {
        const d = new Date(m.fecha);
        const week = Math.ceil(d.getDate() / 7);
        const key = `Sem ${week}`;
        semanas[key] = (semanas[key] || 0) + (parseFloat(m.monto) || 0);
      });
      return Object.entries(semanas).map(([date, ventas]) => ({ date, ventas: Math.round(ventas) }));
    }
    // Year: by month
    const meses = {};
    Object.values(movimientos || {}).filter(m => m.tipo === 'venta').forEach(m => {
      if (!m.fecha) return;
      const key = m.fecha.slice(0, 7);
      meses[key] = (meses[key] || 0) + (parseFloat(m.monto) || 0);
    });
    return Object.entries(meses).sort((a, b) => a[0].localeCompare(b[0])).slice(-12).map(([date, ventas]) => ({
      date: date.split('-')[1] + '/' + date.split('-')[0].slice(2),
      ventas: Math.round(ventas)
    }));
  }, [movimientos, vista, filtroMes, prom20]);

  // Trend: compare last 2 periods
  const trend = useMemo(() => {
    const sorted = chartData.map(d => d.ventas);
    if (sorted.length < 2) return 0;
    const half = Math.floor(sorted.length / 2);
    const first = sorted.slice(0, half).reduce((a, b) => a + b, 0) / half;
    const second = sorted.slice(half).reduce((a, b) => a + b, 0) / (sorted.length - half);
    return first > 0 ? ((second - first) / first) * 100 : 0;
  }, [chartData]);

  // Table
  const ventas = useMemo(() =>
    Object.values(movimientos || {}).filter(m => m.tipo === 'venta' && (!filtroMes || m.fecha?.slice(0, 7) === filtroMes)).sort((a, b) => (b.fecha + (b.hora || '')).localeCompare(a.fecha + (a.hora || ''))),
    [movimientos, filtroMes]);

  function openNew() {
    const hora = new Date().toTimeString().slice(0, 5);
    setForm({ fecha: hoy, hora, ef: '', tr: '', qr: '', turno: 'cierre_dia', obs: '' });
    setModal(true);
  }

  function save() {
    const ef = parseFloat(form.ef) || 0;
    const tr = parseFloat(form.tr) || 0;
    const qr = parseFloat(form.qr) || 0;
    const total = ef + tr + qr;
    if (!total) { showToast('Ingresa al menos un monto'); return; }

    const turnoLabels = { cierre_dia: 'Cierre del día', cierre_manana: 'Cierre mañana', cierre_tarde: 'Cierre tarde', venta_externa: 'Venta externa', otro: 'Otro' };

    // Save as single movement with breakdown
    const id = 'm' + Date.now();
    setItem('movimientos', id, {
      id, tipo: 'venta', fecha: form.fecha, hora: form.hora, monto: total,
      monto_efectivo: ef, monto_transferencia: tr, monto_qr: qr,
      medio: ef > 0 && tr === 0 && qr === 0 ? 'efectivo' : 'mixto',
      signo: '+', turno: form.turno,
      desc: turnoLabels[form.turno] + (form.obs ? ' — ' + form.obs : ''),
      autor: 'panel'
    });
    showToast('Venta registrada');
    setModal(false);
  }

  return (
    <div>
      <StatRow stats={[
        { label: 'Ventas del mes', value: fmtM(mesMonto), color: 'var(--grn)', sub: filtroMes },
        { label: 'Promedio diario 20d', value: fmtM(prom20), sub: 'Últimos 20 días' },
        { label: 'Proyección 30d', value: fmtM(prom20 * 30), sub: 'En base al promedio' },
        { label: 'Tendencia', value: (trend >= 0 ? '+' : '') + trend.toFixed(1) + '%', color: trend >= 0 ? 'var(--grn)' : 'var(--red)', sub: 'vs período anterior' },
      ]} />

      {/* Vista selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
        {[['semana', '7 días'], ['mes', 'Por mes'], ['anio', 'Por año']].map(([val, label]) => (
          <button key={val} onClick={() => setVista(val)}
            style={{ padding: '5px 14px', borderRadius: 99, border: `1px solid ${vista === val ? 'var(--grn)' : 'var(--border2)'}`, background: vista === val ? 'var(--grn-l)' : 'var(--surf2)', color: vista === val ? 'var(--grn)' : 'var(--tx2)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {label}
          </button>
        ))}
        {vista === 'mes' && (
          <input type="month" value={filtroMes} onChange={e => setFiltroMes(e.target.value)}
            style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid var(--border2)', fontSize: 12, background: 'var(--surf)', color: 'var(--tx)' }} />
        )}
        <div style={{ flex: 1 }} />
        <Btn onClick={() => exportToCSV('ventas', ['Fecha','Hora','Descripcion','Efectivo','Transferencia/QR','Total'], ventas.map(m => [m.fecha, m.hora||'', m.desc||'', m.monto_efectivo||0, (m.monto_transferencia||0)+(m.monto_qr||0), m.monto]))}>⬇ CSV</Btn>
<Btn variant="grn" onClick={openNew}>+ Registrar venta</Btn>
      </div>

      {/* Chart */}
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 12 }}>
          Evolución de ventas {trend !== 0 && <span style={{ color: trend >= 0 ? 'var(--grn)' : 'var(--red)', marginLeft: 8 }}>{trend >= 0 ? '▲' : '▼'} {Math.abs(trend).toFixed(1)}%</span>}
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
            <defs>
              <linearGradient id="gVentas" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1A6B45" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#1A6B45" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--tx3)' }} />
            <YAxis tick={{ fontSize: 9, fill: 'var(--tx3)' }} tickFormatter={v => '$' + (v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v)} width={55} />
            <Tooltip formatter={v => fmtM(v)} contentStyle={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 }} />
            {vista === 'semana' && prom20 > 0 && <ReferenceLine y={prom20} stroke="#1A4E76" strokeDasharray="4 4" label={{ value: 'Prom', fontSize: 9, fill: 'var(--blu-m)' }} />}
            <Area type="monotone" dataKey="ventas" stroke="#1A6B45" fill="url(#gVentas)" strokeWidth={2} dot={{ fill: '#1A6B45', r: 3 }} activeDot={{ r: 5 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <SectionHeader title="Detalle de ventas" action={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="month" value={filtroMes} onChange={e => setFiltroMes(e.target.value)}
            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border2)', fontSize: 11, background: 'var(--surf)', color: 'var(--tx)' }} />
        </div>
      } />
      <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--surf2)' }}>
              {['Fecha', 'Hora', 'Descripción', 'Efectivo', 'Cuentas', 'Total', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '9px 13px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ventas.length === 0
              ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--tx3)' }}>Sin ventas en este período</td></tr>
              : ventas.map(m => (
                <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontSize: 11, color: 'var(--tx3)' }}>{fmtDate(m.fecha)}</td>
                  <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontSize: 11, color: 'var(--tx3)' }}>{m.hora || '—'}</td>
                  <td style={{ padding: '9px 13px', fontSize: 12 }}>{m.desc || '—'}</td>
                  <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontSize: 11, color: 'var(--grn)' }}>{m.monto_efectivo > 0 ? fmtM(m.monto_efectivo) : '—'}</td>
                  <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontSize: 11, color: 'var(--blu-m)' }}>{(m.monto_transferencia > 0 || m.monto_qr > 0) ? fmtM((m.monto_transferencia || 0) + (m.monto_qr || 0) * 0.99) : '—'}</td>
                  <td style={{ padding: '9px 13px', fontFamily: 'Courier, monospace', fontWeight: 700, color: 'var(--grn)' }}>{fmtM(m.monto)}</td>
                  <td style={{ padding: '9px 13px' }}>
                    <Btn size="sm" variant="red" onClick={() => { delItem('movimientos', m.id); showToast('Eliminado'); }}>×</Btn>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title="Registrar venta / cierre de caja" onClose={() => setModal(false)} onSave={save}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
            <Field label="Fecha">
              <Input value={form.fecha} onChange={v => setForm(f => ({ ...f, fecha: v }))} type="date" />
            </Field>
            <Field label="Hora">
              <Input value={form.hora} onChange={v => setForm(f => ({ ...f, hora: v }))} type="time" />
            </Field>
            <Field label="Efectivo" full={false}>
              <Input value={form.ef} onChange={v => setForm(f => ({ ...f, ef: v }))} type="number" placeholder="0" autoFocus />
            </Field>
            <Field label="Transferencia">
              <Input value={form.tr} onChange={v => setForm(f => ({ ...f, tr: v }))} type="number" placeholder="0" />
            </Field>
            <Field label="QR / MP">
              <Input value={form.qr} onChange={v => setForm(f => ({ ...f, qr: v }))} type="number" placeholder="0" />
            </Field>
            <Field label="Turno">
              <Select value={form.turno} onChange={v => setForm(f => ({ ...f, turno: v }))}>
                <option value="cierre_dia">Cierre del día</option>
                <option value="cierre_manana">Cierre mañana</option>
                <option value="cierre_tarde">Cierre tarde</option>
                <option value="venta_externa">Venta externa</option>
                <option value="otro">Otro</option>
              </Select>
            </Field>
            <Field label="Observaciones" full>
              <Input value={form.obs} onChange={v => setForm(f => ({ ...f, obs: v }))} placeholder="Opcional..." />
            </Field>
          </div>
          {(parseFloat(form.ef) || 0) + (parseFloat(form.tr) || 0) + (parseFloat(form.qr) || 0) > 0 && (
            <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--grn-l)', borderRadius: 8, fontSize: 13, fontWeight: 700, color: 'var(--grn)', fontFamily: 'Courier, monospace' }}>
              Total: {fmtM((parseFloat(form.ef) || 0) + (parseFloat(form.tr) || 0) + (parseFloat(form.qr) || 0))}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
